import { Box, Flex, Group, Paper, Text, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import useDashboardStore from "../../store/useDashboardStore";
import { InfoTooltip } from "../../shared/components/InfoTooltip";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";

export const Statistics = () => {
  const mobile = useMediaQuery("(max-width: 820px)");
  const matches = useMediaQuery("(max-width: 650px)");
  const { t } = useTranslation();

  const { dashboard } = useDashboardStore();

  const statistics = dashboard?.statistics;

  const fieldLabels: Record<string, string> = {
    avatar: t("avatar"),
    certificate: t("certificate"),
    cv: t("cv"),
    email: t("email"),
    education: t("education"),
    experience: t("experience"),
    facebook: t("facebook"),
    language: t("language"),
    linkedin: t("linkedin"),
    location: t("location"),
    phone: t("phone"),
    skills: t("skills"),
    survey: t("profileEdit.goals"),
    twitter: t("twitter"),
  };

  const missingFields = dashboard?.statistics.details
    ? Object.entries(dashboard.statistics.details)
        .filter(([_, value]) => value === false)
        .map(([key]) => fieldLabels[key] || key)
    : [];

  // Формуємо додатковий текст, якщо профіль не заповнений на 100%
  const additionalHint =
    dashboard?.statistics.profile_completed !== 100 && missingFields.length > 0
      ? `${t("missing_fields_prefix")} ${missingFields.join(", ")}`
      : "";
  return matches ? (
    <Paper withBorder radius="24px" p={32} h="100%">
      <OnboardingTour.Target id="dashboard-statistic">
        <Title size={36}>{t("statistics_title")}</Title>
      </OnboardingTour.Target>
      <Flex mt={40} wrap="wrap" gap={mobile ? 24 : 32} justify="space-between">
        <Flex
          align="center"
          gap={11}
          style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
        >
          <Title size={36}>🔥</Title>
          <Box>
            <Title size={24} mb={8}>
              {statistics?.day_streak}
            </Title>
            <Group gap={0} align="center" wrap="nowrap">
              <Text>{t("day_streak")}</Text>
              <InfoTooltip label={t("streak_description")} size={20} />
            </Group>
          </Box>
        </Flex>

        <Flex
          align="center"
          gap={11}
          style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
        >
          <Title size={36}>🧑‍🏫</Title>
          <Box>
            <Title size={24} mb={8}>
              {statistics?.total_goals}
            </Title>
            <Group gap={0} align="center" wrap="nowrap">
              <Text>{t("minutes_coaching")}</Text>
              <InfoTooltip label={t("goals_created_description")} size={20} />
            </Group>
          </Box>
        </Flex>

        <Flex
          align="center"
          gap={11}
          style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
        >
          <Title size={36}>🧑</Title>
          <Box>
            <Title size={24} mb={8}>
              {statistics?.profile_completed}%
            </Title>
            <Group gap={0} align="center" wrap="nowrap">
              <Text>{t("profile_completed")}</Text>
              <InfoTooltip
                label={`${t("profile_completion_description")} 
              ${additionalHint ? `${additionalHint}` : ""}`}
                size={20}
              />
            </Group>
          </Box>
        </Flex>

        <Flex
          align="center"
          gap={11}
          style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
        >
          <Title size={36}>🏆</Title>
          <Box>
            <Title size={24} mb={8}>
              {statistics?.goals_achieved}
            </Title>
            <Group gap={0} align="center" wrap="nowrap">
              <Text>{t("goals_achieved")}</Text>

              <InfoTooltip label={t("goals_completed_description")} size={20} />
            </Group>
          </Box>
        </Flex>
      </Flex>
    </Paper>
  ) : (
    <OnboardingTour.Target id="dashboard-statistic">
      <Paper withBorder radius="24px" p={32} h="100%">
        <Title size={36}>{t("statistics_title")}</Title>
        <Flex
          mt={40}
          wrap="wrap"
          gap={mobile ? 24 : 32}
          justify="space-between"
        >
          <Flex
            align="center"
            gap={11}
            style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
          >
            <Title size={36}>🔥</Title>
            <Box>
              <Title size={24} mb={8}>
                {statistics?.day_streak}
              </Title>
              <Group gap={0} align="center" wrap="nowrap">
                <Text>{t("day_streak")}</Text>
                <InfoTooltip label={t("streak_description")} size={20} />
              </Group>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap={11}
            style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
          >
            <Title size={36}>🧑‍🏫</Title>
            <Box>
              <Title size={24} mb={8}>
                {statistics?.total_goals}
              </Title>
              <Group gap={0} align="center" wrap="nowrap">
                <Text>{t("minutes_coaching")}</Text>
                <InfoTooltip label={t("goals_created_description")} size={20} />
              </Group>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap={11}
            style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
          >
            <Title size={36}>🧑</Title>
            <Box>
              <Title size={24} mb={8}>
                {statistics?.profile_completed}%
              </Title>
              <Group gap={0} align="center" wrap="nowrap">
                <Text>{t("profile_completed")}</Text>
                <InfoTooltip
                  label={`${t("profile_completion_description")} 
                ${additionalHint ? `${additionalHint}` : ""}`}
                  size={20}
                />
              </Group>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap={11}
            style={{ minWidth: mobile ? "100%" : "calc(50% - 18px)" }}
          >
            <Title size={36}>🏆</Title>
            <Box>
              <Title size={24} mb={8}>
                {statistics?.goals_achieved}
              </Title>
              <Group gap={0} align="center" wrap="nowrap">
                <Text>{t("goals_achieved")}</Text>

                <InfoTooltip
                  label={t("goals_completed_description")}
                  size={20}
                />
              </Group>
            </Box>
          </Flex>
        </Flex>
      </Paper>
    </OnboardingTour.Target>
  );
};
